import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Users, Activity, FileText, MessageSquare, LogOut, Shield, Plus,
  Edit, Trash2, DollarSign, Send, Search, Download, Eye, X, MapPin,
  Phone, Mail, CreditCard, Building, ChevronDown, ChevronUp, Ticket,
  Bell, TrendingUp, AlertCircle, CheckCircle, Clock, Filter, Upload, Settings,
  Package, Database, Printer, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import IndiaLiveClock from '../../components/IndiaLiveClock';
import NotificationBell from '../../components/NotificationBell';
import AddCustomerModal from '../../components/dashboard/AddCustomerModal';
import BulkUploadModal from '../../components/dashboard/BulkUploadModal';
import EditCustomerModal from '../../components/dashboard/EditCustomerModal';
import DocumentUploadModal from '../../components/dashboard/DocumentUploadModal';
import SupportTicketsManagement from './SupportTicketsManagement';
import CreateTicketModal from '../../components/dashboard/CreateTicketModal';
import BillingManagement from './BillingManagement';
import OrderManagement from './OrderManagement';
import CustomersManagement from './CustomersManagement';
import Dashboard from './Dashboard';
import DataManagement from './DataManagement';
import SetCredentialsModal from '../../components/dashboard/SetCredentialsModal';
import DeleteCustomerModal from '../../components/dashboard/DeleteCustomerModal';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CustomerAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [swipeApiKey, setSwipeApiKey] = useState('');
  const [swipeConnected, setSwipeConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncingCustomers, setSyncingCustomers] = useState(false);
  const [customerSyncResult, setCustomerSyncResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  
  // New Features State
  const [serviceMetrics, setServiceMetrics] = useState(null);
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState({ status: '', category: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [bulkEmailForm, setBulkEmailForm] = useState({ subject: '', message: '', filters: {} });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Customer Management Modals
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [showDeleteInvoiceModal, setShowDeleteInvoiceModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [showSetCredentialsModal, setShowSetCredentialsModal] = useState(false);
  const [customerForCredentials, setCustomerForCredentials] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('customerAdminToken');
    if (!token) {
      navigate('/customer-admin/login');
      return;
    }
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      const [statsRes, customersRes, plansRes, invoicesRes, metricsRes, ticketsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/customer-admin/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/customer-admin/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/customer-admin/plans`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/customer-admin/invoices-all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/customer-admin/service-metrics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/customer-admin/support-tickets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) setDashboardStats(await statsRes.json());
      if (customersRes.ok) setCustomers((await customersRes.json()).customers);
      if (plansRes.ok) setPlans((await plansRes.json()).plans);
      if (invoicesRes.ok) setInvoices((await invoicesRes.json()).invoices);
      if (metricsRes.ok) setServiceMetrics(await metricsRes.json());
      if (ticketsRes.ok) {
        const ticketData = await ticketsRes.json();
        setSupportTickets(ticketData.tickets || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerAdminToken');
    localStorage.removeItem('isCustomerAdmin');
    localStorage.removeItem('customerAdmin');
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Handle notification navigation
  const handleNotificationNavigate = (tab, relatedId) => {
    // Set the active tab
    setActiveTab(tab);
    
    // Optional: Store the relatedId in state to highlight/filter the specific item
    // You can add additional logic here to scroll to or highlight the specific item
    if (relatedId) {
      // Add a small delay to ensure tab content is rendered
      setTimeout(() => {
        toast.info(`Navigated to ${tab}. Looking for: ${relatedId}`);
      }, 500);
    }
  };

  const testSwipeConnection = async () => {
    const token = localStorage.getItem('customerAdminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/swipe-connection-test`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSwipeConnected(data.connected);
      toast(data.message, { type: data.connected ? 'success' : 'error' });
    } catch (error) {
      toast.error('Failed to test Swipe connection');
    }
  };

  const syncSwipeInvoices = async () => {
    const token = localStorage.getItem('customerAdminToken');
    setSyncing(true);
    setSyncResult(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/sync-swipe-invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSyncResult(data);
        toast.success(`Synced ${data.synced} invoices successfully!`);
        
        // Refresh invoices
        const invoicesRes = await fetch(`${API_BASE_URL}/api/customer-admin/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (invoicesRes.ok) {
          setInvoices((await invoicesRes.json()).allInvoices);
        }
      } else {
        const errorMessage = data.detail || data.message || 'Failed to sync invoices';
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Sync error: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const syncSwipeCustomers = async () => {
    const token = localStorage.getItem('customerAdminToken');
    setSyncingCustomers(true);
    setCustomerSyncResult(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/sync-swipe-customers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setCustomerSyncResult(data);
        toast.success(`Synced ${data.synced} customers successfully!`);
        
        // Refresh customers and stats
        const customersRes = await fetch(`${API_BASE_URL}/api/customer-admin/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (customersRes.ok) {
          setCustomers((await customersRes.json()).customers);
        }
        
        const statsRes = await fetch(`${API_BASE_URL}/api/customer-admin/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statsRes.ok) {
          setDashboardStats(await statsRes.json());
        }
      } else {
        const errorMessage = data.detail || data.message || 'Failed to sync customers';
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Sync error: ' + error.message);
    } finally {
      setSyncingCustomers(false);
    }
  };

  const migrateAccountIds = async () => {
    const token = localStorage.getItem('customerAdminToken');
    setMigrating(true);
    setMigrationResult(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/migrate-account-ids`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMigrationResult(data);
        toast.success(`Migrated ${data.updated} account IDs successfully!`);
        
        // Refresh customers
        const customersRes = await fetch(`${API_BASE_URL}/api/customer-admin/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (customersRes.ok) {
          setCustomers((await customersRes.json()).customers);
        }
      } else {
        const errorMessage = data.detail || data.message || 'Failed to migrate account IDs';
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Migration error: ' + error.message);
    } finally {
      setMigrating(false);
    }
  };


  // INVOICE MANAGEMENT HANDLERS
  const handleViewInvoice = async (invoice) => {
    const token = localStorage.getItem('customerAdminToken');
    try {
      // Download and open the invoice in a new tab
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/download-document/${invoice.filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        toast.error('Failed to view invoice');
      }
    } catch (error) {
      console.error('Error viewing invoice:', error);
      toast.error('Error viewing invoice: ' + error.message);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    const token = localStorage.getItem('customerAdminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/download-document/${invoice.filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = invoice.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Invoice downloaded successfully!');
      } else {
        toast.error('Failed to download invoice');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Error downloading invoice: ' + error.message);
    }
  };

  const handleDeleteInvoiceClick = (invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteInvoiceModal(true);
  };

  const handleConfirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/invoices/${invoiceToDelete.invoiceNumber}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Invoice deleted successfully!');
        // Remove the invoice from the list
        setInvoices(invoices.filter(inv => inv.invoiceNumber !== invoiceToDelete.invoiceNumber));
        setShowDeleteInvoiceModal(false);
        setInvoiceToDelete(null);
      } else {
        toast.error(data.detail || 'Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Error deleting invoice: ' + error.message);
    }
  };

  // NEW FEATURE HANDLERS

  const updateTicket = async (ticketId, updateData) => {
    const token = localStorage.getItem('customerAdminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/support-tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Ticket updated successfully');
        // Refresh tickets
        const ticketsRes = await fetch(`${API_BASE_URL}/api/customer-admin/support-tickets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (ticketsRes.ok) {
          const ticketData = await ticketsRes.json();
          setSupportTickets(ticketData.tickets || []);
        }
        setSelectedTicket(null);
        setTicketReply('');
      } else {
        toast.error('Failed to update ticket');
      }
    } catch (error) {
      toast.error('Error updating ticket: ' + error.message);
    }
  };

  const sendBulkEmail = async () => {
    const token = localStorage.getItem('customerAdminToken');
    setSendingEmail(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/send-bulk-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: bulkEmailForm.subject,
          message: bulkEmailForm.message,
          filters: bulkEmailForm.filters
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Email sent to ${data.sent} customers successfully!`);
        setBulkEmailForm({ subject: '', message: '', filters: {} });
      } else {
        toast.error(data.detail || 'Failed to send emails');
      }
    } catch (error) {
      toast.error('Error sending emails: ' + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const sendPaymentReminders = async () => {
    const token = localStorage.getItem('customerAdminToken');
    setSendingReminders(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/send-payment-reminders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Sent ${data.sent} payment reminders successfully!`);
      } else {
        toast.error(data.detail || 'Failed to send reminders');
      }
    } catch (error) {
      toast.error('Error sending reminders: ' + error.message);
    } finally {
      setSendingReminders(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      customer.firstName?.toLowerCase().includes(search) ||
      customer.lastName?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search) ||
      customer.mobile?.includes(search) ||
      customer.accountId?.toLowerCase().includes(search) ||
      customer.gstin?.toLowerCase().includes(search)
    );
  });

  const CustomerDetailModal = ({ customer, onClose }) => {
    if (!customer) return null;

    const customerInvoices = invoices.filter(inv => 
      inv.customerId === customer.email || inv.customerId === customer._id
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">{customer.firstName} {customer.lastName}</h2>
              <p className="text-emerald-100">{customer.accountId}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => {
                  setCustomerToEdit({
                    id: customer._id,
                    email: customer.email,
                    firstName: customer.firstName,
                    lastName: customer.lastName,
                    mobile: customer.mobile,
                    companyName: customer.companyName,
                    gstin: customer.gstin,
                    billingAddress: customer.billingAddress,
                    shippingAddress: customer.shippingAddress,
                    outstandingAmount: customer.outstandingAmount || 0
                  });
                  setShowEditCustomerModal(true);
                  onClose();
                }}
                className="bg-white text-emerald-600 hover:bg-emerald-50"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Customer
              </Button>
              <button onClick={onClose} className="text-white hover:text-gray-200">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Personal Information */}
            <Card>
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Email</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <p className="font-medium">{customer.email}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-600">Mobile</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <p className="font-medium">{customer.mobile || 'N/A'}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-600">Account ID</Label>
                    <p className="font-medium">{customer.accountId || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {customer.status}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GST & Billing Information */}
            {(customer.gstin || customer.billingAddress) && (
              <Card>
                <CardHeader className="bg-purple-50">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    GST & Billing Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {customer.gstin && (
                      <div>
                        <Label className="text-gray-600">GSTIN</Label>
                        <p className="font-medium">{customer.gstin}</p>
                      </div>
                    )}
                    {customer.placeOfSupply && (
                      <div>
                        <Label className="text-gray-600">Place of Supply</Label>
                        <p className="font-medium">{customer.placeOfSupply}</p>
                      </div>
                    )}
                    {customer.taxRegistrationType && (
                      <div>
                        <Label className="text-gray-600">Tax Registration Type</Label>
                        <p className="font-medium">{customer.taxRegistrationType}</p>
                      </div>
                    )}
                    {customer.swipePartyId && (
                      <div>
                        <Label className="text-gray-600">Swipe Party ID</Label>
                        <p className="font-medium text-xs">{customer.swipePartyId}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Billing Address */}
            {customer.billingAddress && (
              <Card>
                <CardHeader className="bg-amber-50">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Billing Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {customer.billingAddress.address_line1 && (
                      <p className="text-sm">{customer.billingAddress.address_line1}</p>
                    )}
                    {customer.billingAddress.address_line2 && (
                      <p className="text-sm">{customer.billingAddress.address_line2}</p>
                    )}
                    <p className="text-sm">
                      {[
                        customer.billingAddress.city,
                        customer.billingAddress.state,
                        customer.billingAddress.pincode
                      ].filter(Boolean).join(', ')}
                    </p>
                    {customer.billingAddress.country && (
                      <p className="text-sm font-medium">{customer.billingAddress.country}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shipping Address */}
            {customer.shippingAddress && (
              <Card>
                <CardHeader className="bg-teal-50">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Shipping Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {customer.shippingAddress.address_line1 && (
                      <p className="text-sm">{customer.shippingAddress.address_line1}</p>
                    )}
                    {customer.shippingAddress.address_line2 && (
                      <p className="text-sm">{customer.shippingAddress.address_line2}</p>
                    )}
                    <p className="text-sm">
                      {[
                        customer.shippingAddress.city,
                        customer.shippingAddress.state,
                        customer.shippingAddress.pincode
                      ].filter(Boolean).join(', ')}
                    </p>
                    {customer.shippingAddress.country && (
                      <p className="text-sm font-medium">{customer.shippingAddress.country}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Invoices */}
            {customerInvoices.length > 0 && (
              <Card>
                <CardHeader className="bg-green-50">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Invoices ({customerInvoices.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {customerInvoices.map(invoice => (
                      <div key={invoice._id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                        <div>
                          <p className="font-bold text-emerald-600">#{invoice.invoiceNumber}</p>
                          <p className="text-sm text-gray-600">
                            Due: {new Date(invoice.dueDate).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">₹{invoice.totalAmount}</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {invoice.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sync Information */}
            {customer.swipeSyncedAt && (
              <div className="text-center text-sm text-gray-500">
                Last synced from Swipe: {new Date(customer.swipeSyncedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-lg p-2 shadow-lg">
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-black bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">NETIX</span>
                  <span className="text-xs font-semibold text-emerald-600 -mt-1">FIBERNET</span>
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold">Customer Admin Panel</h1>
                <p className="text-emerald-100">Netix Fibernet Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <IndiaLiveClock />
              <NotificationBell onNavigate={handleNotificationNavigate} />
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white text-emerald-600 border-white hover:bg-emerald-100"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b-2 border-emerald-200 shadow-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Activity },
              { id: 'customers', label: 'Customers', icon: Users },
              { id: 'billing', label: 'Account & Billing', icon: CreditCard },
              { id: 'orders', label: 'Order Management', icon: Package },
              { id: 'invoices', label: 'Invoices', icon: FileText },
              { id: 'tickets', label: 'Support Tickets', icon: Ticket },
              { id: 'communications', label: 'Communications', icon: Send },
              { id: 'data-management', label: 'Data & Reports', icon: Database }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-3 text-sm font-semibold transition-all border-b-4 flex items-center gap-1.5 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-emerald-600 text-emerald-600 bg-emerald-50'
                      : 'border-transparent text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <Dashboard onNavigate={setActiveTab} />
        )}

        {/* OLD Dashboard - REMOVED */}
        {false && activeTab === 'old-dashboard' && (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Overview</h2>
            
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="border-2 border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Customers</p>
                      <p className="text-3xl font-bold text-blue-600">{dashboardStats?.totalCustomers || 0}</p>
                    </div>
                    <Users className="h-12 w-12 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Plans</p>
                      <p className="text-3xl font-bold text-green-600">{dashboardStats?.activePlans || 0}</p>
                    </div>
                    <Activity className="h-12 w-12 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-yellow-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Pending Invoices</p>
                      <p className="text-3xl font-bold text-yellow-600">{dashboardStats?.pendingInvoices || 0}</p>
                    </div>
                    <FileText className="h-12 w-12 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Revenue</p>
                      <p className="text-3xl font-bold text-purple-600">
                        ₹{(invoices.reduce((sum, inv) => sum + (inv.status === 'Paid' ? inv.totalAmount : 0), 0)).toFixed(2)}
                      </p>
                    </div>
                    <DollarSign className="h-12 w-12 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Service Status Dashboard */}
            {serviceMetrics && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="h-6 w-6" />
                  Service Status & Metrics
                </h2>

                {/* Active vs Inactive */}
                <div className="grid md:grid-cols-3 gap-6">
                  <Card className="border-2 border-green-200">
                    <CardHeader className="bg-green-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Active Services
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-4xl font-bold text-green-600">
                        {serviceMetrics.activeVsInactive?.active || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        of {serviceMetrics.activeVsInactive?.total || 0} total customers
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-red-200">
                    <CardHeader className="bg-red-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" />
                        Inactive Services
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-4xl font-bold text-red-600">
                        {serviceMetrics.activeVsInactive?.inactive || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">Need attention</p>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-blue-200">
                    <CardHeader className="bg-blue-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Plus className="h-5 w-5" />
                        New This Month
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-4xl font-bold text-blue-600">
                        {serviceMetrics.newConnectionsThisMonth || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">New connections</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Expiring Services */}
                <Card className="border-2 border-orange-200">
                  <CardHeader className="bg-orange-50">
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Expiring Services
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <p className="text-3xl font-bold text-red-600">
                          {serviceMetrics.expiringServices?.next7Days?.count || 0}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Next 7 Days</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <p className="text-3xl font-bold text-yellow-600">
                          {serviceMetrics.expiringServices?.next15Days?.count || 0}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Next 15 Days</p>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <p className="text-3xl font-bold text-orange-600">
                          {serviceMetrics.expiringServices?.next30Days?.count || 0}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Next 30 Days</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Revenue Metrics */}
                <Card className="border-2 border-purple-200">
                  <CardHeader className="bg-purple-50">
                    <CardTitle className="text-xl flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Revenue Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-gray-600">Total Collected</p>
                        <p className="text-2xl font-bold text-green-600">
                          ₹{serviceMetrics.revenue?.collected?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <p className="text-sm text-gray-600">Pending</p>
                        <p className="text-2xl font-bold text-yellow-600">
                          ₹{serviceMetrics.revenue?.pending?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-purple-600">
                          ₹{serviceMetrics.revenue?.total?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Plan Breakdown */}
                {serviceMetrics.planBreakdown && Object.keys(serviceMetrics.planBreakdown).length > 0 && (
                  <Card className="border-2 border-indigo-200">
                    <CardHeader className="bg-indigo-50">
                      <CardTitle className="text-xl">Plan-wise Distribution</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid md:grid-cols-4 gap-4">
                        {Object.entries(serviceMetrics.planBreakdown).map(([plan, count]) => (
                          <div key={plan} className="p-3 bg-gray-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-indigo-600">{count}</p>
                            <p className="text-sm text-gray-600 mt-1">{plan}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Payment Reminders Button */}
                <Card className="border-2 border-teal-200">
                  <CardHeader className="bg-teal-50">
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Automated Payment Reminders
                    </CardTitle>
                    <CardDescription>
                      Send reminders for invoices due in 5 days or overdue by 3+ days
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <Button
                      onClick={sendPaymentReminders}
                      disabled={sendingReminders}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {sendingReminders ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Bell className="h-4 w-4 mr-2" />
                          Send Payment Reminders Now
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Recent Customers */}
            <Card className="border-2 border-emerald-200 shadow-xl">
              <CardHeader className="bg-emerald-50">
                <CardTitle className="text-2xl">Recent Customers</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {dashboardStats?.recentCustomers?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardStats.recentCustomers.map((customer) => (
                      <div 
                        key={customer._id} 
                        className="flex items-center justify-between p-4 bg-white border-2 rounded-lg hover:border-emerald-300 cursor-pointer transition-all"
                        onClick={() => setSelectedCustomer(customer)}
                      >
                        <div>
                          <p className="font-bold">{customer.firstName} {customer.lastName}</p>
                          <p className="text-sm text-gray-600">{customer.email}</p>
                          {customer.gstin && (
                            <p className="text-xs text-gray-500 mt-1">GST: {customer.gstin}</p>
                          )}
                        </div>
                        <div className="text-right">
                          {customer.accountId && (
                            <p className="text-xs font-medium text-emerald-600">{customer.accountId}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            {new Date(customer.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No recent customers</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <CustomersManagement />
        )}

        {/* OLD Customers Tab - REMOVED */}
        {false && activeTab === 'customers' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-bold text-gray-900">Customer Management</h2>
              <div className="flex gap-2">
                <Input
                  type="search"
                  placeholder="Search by name, email, mobile, GST..."
                  className="w-80"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button 
                  onClick={() => setShowBulkUploadModal(true)}
                  className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Upload
                </Button>
                <Button 
                  onClick={() => setShowAddCustomerModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </div>
            </div>

            <Card className="border-2 border-emerald-200 shadow-xl">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {filteredCustomers.map((customer) => (
                    <div key={customer._id}>
                      <div 
                        className="flex items-center justify-between p-4 bg-white border-2 rounded-lg hover:border-emerald-300 cursor-pointer transition-all"
                        onClick={() => setExpandedCustomer(expandedCustomer === customer._id ? null : customer._id)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="bg-emerald-100 p-2 rounded-full">
                              <Users className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-bold text-lg">{customer.firstName} {customer.lastName}</p>
                              <div className="flex gap-4 text-sm text-gray-600 mt-1">
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {customer.email}
                                </span>
                                {customer.mobile && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {customer.mobile}
                                  </span>
                                )}
                              </div>
                              {customer.gstin && (
                                <p className="text-xs text-purple-600 mt-1 font-medium">GST: {customer.gstin}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            {customer.accountId && (
                              <p className="text-sm font-bold text-emerald-600">{customer.accountId}</p>
                            )}
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {customer.status}
                            </span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="bg-green-50 hover:bg-green-100 text-green-600 border-green-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCustomerForCredentials(customer);
                              setShowSetCredentialsModal(true);
                            }}
                          >
                            <Mail className="h-4 w-4 mr-1" />
                            Set Login
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="bg-orange-50 hover:bg-orange-100 text-orange-600 border-orange-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCustomerToEdit({
                                id: customer._id,
                                email: customer.email,
                                firstName: customer.firstName,
                                lastName: customer.lastName,
                                mobile: customer.mobile,
                                companyName: customer.companyName,
                                gstin: customer.gstin,
                                billingAddress: customer.billingAddress,
                                shippingAddress: customer.shippingAddress,
                                outstandingAmount: customer.outstandingAmount || 0
                              });
                              setShowEditCustomerModal(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCustomer(customer);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="bg-red-50 hover:bg-red-100 text-red-600 border-red-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCustomerToDelete(customer);
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {expandedCustomer === customer._id ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Expanded View */}
                      {expandedCustomer === customer._id && (
                        <div className="ml-4 mt-2 p-4 bg-gray-50 rounded-lg border-l-4 border-emerald-500">
                          <div className="grid md:grid-cols-2 gap-4">
                            {customer.billingAddress && (
                              <div>
                                <p className="text-xs font-semibold text-gray-600 mb-1">BILLING ADDRESS</p>
                                <div className="text-sm">
                                  {customer.billingAddress.address_line1 && <p>{customer.billingAddress.address_line1}</p>}
                                  {customer.billingAddress.address_line2 && <p>{customer.billingAddress.address_line2}</p>}
                                  <p>{[customer.billingAddress.city, customer.billingAddress.state, customer.billingAddress.pincode].filter(Boolean).join(', ')}</p>
                                </div>
                              </div>
                            )}
                            {customer.placeOfSupply && (
                              <div>
                                <p className="text-xs font-semibold text-gray-600 mb-1">PLACE OF SUPPLY</p>
                                <p className="text-sm">{customer.placeOfSupply}</p>
                              </div>
                            )}
                            {customer.taxRegistrationType && (
                              <div>
                                <p className="text-xs font-semibold text-gray-600 mb-1">TAX REGISTRATION</p>
                                <p className="text-sm">{customer.taxRegistrationType}</p>
                              </div>
                            )}
                            {customer.swipeSyncedAt && (
                              <div>
                                <p className="text-xs font-semibold text-gray-600 mb-1">LAST SYNCED</p>
                                <p className="text-sm">{new Date(customer.swipeSyncedAt).toLocaleString()}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}


        {/* Account & Billing Tab */}
        {activeTab === 'billing' && (
          <BillingManagement />
        )}

        {/* Order Management Tab */}
        {activeTab === 'orders' && (
          <OrderManagement />
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-bold text-gray-900">Invoice Management</h2>
              <div className="text-sm text-gray-600">
                Total Invoices: <span className="font-bold text-emerald-600">{invoices.length}</span>
              </div>
            </div>

            <Card className="border-2 border-emerald-200 shadow-xl">
              <CardContent className="pt-6">
                {invoices.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Invoices Found</h3>
                    <p className="text-gray-500">Invoices will appear here once uploaded in Billing Management</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-emerald-200 bg-emerald-50">
                          <th className="text-left p-4 font-bold">Invoice #</th>
                          <th className="text-left p-4 font-bold">Customer Name</th>
                          <th className="text-left p-4 font-bold">Invoice Amount</th>
                          <th className="text-left p-4 font-bold">Due Date</th>
                          <th className="text-center p-4 font-bold">Invoice Status</th>
                          <th className="text-center p-4 font-bold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice, idx) => (
                          <tr key={idx} className="border-b hover:bg-emerald-50 transition-colors">
                            <td className="p-4">
                              <p className="font-bold text-emerald-600 text-lg">#{invoice.invoiceNumber}</p>
                            </td>
                            <td className="p-4">
                              <p className="font-semibold">{invoice.customerName}</p>
                              <p className="text-xs text-gray-500">{invoice.customerEmail}</p>
                            </td>
                            <td className="p-4">
                              <p className="font-bold text-lg">₹{invoice.invoiceAmount?.toLocaleString() || '0'}</p>
                            </td>
                            <td className="p-4">
                              <p className="text-sm">
                                {invoice.dueDate 
                                  ? new Date(invoice.dueDate).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })
                                  : 'N/A'
                                }
                              </p>
                            </td>
                            <td className="p-4 text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {invoice.status}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex justify-center gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleViewInvoice(invoice)}
                                  className="hover:bg-blue-50 hover:border-blue-400"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDownloadInvoice(invoice)}
                                  className="hover:bg-green-50 hover:border-green-400"
                                >
                                  <Download className="h-4 w-4 mr-1" />
                                  Download
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDeleteInvoiceClick(invoice)}
                                  className="hover:bg-red-50 hover:border-red-400 text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Swipe Sync Tab */}
        {activeTab === 'swipe' && (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Swipe Billing Integration</h2>

            <Card className="border-2 border-blue-200 shadow-xl">
              <CardHeader className="bg-blue-50">
                <CardTitle>Connection Status</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-4">
                  <Button onClick={testSwipeConnection} className="bg-blue-600 hover:bg-blue-700">
                    Test Connection
                  </Button>
                  {swipeConnected && (
                    <span className="text-green-600 font-semibold flex items-center gap-2">
                      <span className="h-3 w-3 bg-green-600 rounded-full"></span>
                      Connected
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-emerald-200 shadow-xl">
                <CardHeader className="bg-emerald-50">
                  <CardTitle>Sync Customers</CardTitle>
                  <CardDescription>Import all customers from Swipe Billing</CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <Button 
                    onClick={syncSwipeCustomers} 
                    disabled={syncingCustomers}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {syncingCustomers ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Sync Customers
                      </>
                    )}
                  </Button>

                  {customerSyncResult && (
                    <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                      <p className="font-semibold text-green-800">Sync Complete!</p>
                      <p className="text-sm text-green-700 mt-1">
                        ✓ {customerSyncResult.synced} customers synced
                      </p>
                      <p className="text-sm text-yellow-700">
                        ⊘ {customerSyncResult.skipped} customers skipped
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200 shadow-xl">
                <CardHeader className="bg-purple-50">
                  <CardTitle>Sync Invoices</CardTitle>
                  <CardDescription>Import all invoices from Swipe Billing</CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <Button 
                    onClick={syncSwipeInvoices} 
                    disabled={syncing}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {syncing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Sync Invoices
                      </>
                    )}
                  </Button>

                  {syncResult && (
                    <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                      <p className="font-semibold text-green-800">Sync Complete!</p>
                      <p className="text-sm text-green-700 mt-1">
                        ✓ {syncResult.synced} invoices synced
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="border-2 border-orange-200 shadow-xl">
              <CardHeader className="bg-orange-50">
                <CardTitle>Migrate Account IDs</CardTitle>
                <CardDescription>Update all customer account IDs to new format: NW/SR/AL/03846IN</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <Button 
                  onClick={migrateAccountIds} 
                  disabled={migrating}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  {migrating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Migrating...
                    </>
                  ) : (
                    <>
                      <Edit className="h-4 w-4 mr-2" />
                      Migrate to New Format
                    </>
                  )}
                </Button>

                {migrationResult && (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <p className="font-semibold text-green-800">Migration Complete!</p>
                    <p className="text-sm text-green-700 mt-1">
                      ✓ {migrationResult.updated} account IDs updated
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      New format: NW/SR/AL/03846IN onwards
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Support Tickets Tab */}
        {activeTab === 'tickets' && (
          <SupportTicketsManagement />
        )}

        {/* Support Tickets Tab - OLD (kept for reference but commented) */}
        {false && activeTab === 'tickets' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Ticket className="h-8 w-8" />
                Support Tickets
              </h2>
              <Button
                onClick={() => setShowCreateTicketModal(true)}
                className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Ticket
              </Button>
            </div>
            
            {/* Tickets List */}
            <div className="space-y-3">
              {supportTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-white rounded-lg">
                  No tickets found
                </div>
              ) : (
                supportTickets
                  .filter(ticket => !ticketFilter.status || ticket.status === ticketFilter.status)
                  .map((ticket) => (
                    <div key={ticket._id} className="p-4 bg-white rounded-lg border-2 hover:border-orange-300 cursor-pointer transition-all">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-lg">{ticket.ticketId}</p>
                          <p className="text-gray-700 mt-1">{ticket.subject}</p>
                          <p className="text-sm text-gray-500 mt-1">{ticket.category}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          ticket.status === 'Open' ? 'bg-blue-100 text-blue-800' :
                          ticket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                          ticket.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {ticket.status}
                        </span>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        {/* Communications Tab */}
        {activeTab === 'communications' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h2 className="text-3xl font-bold text-gray-900">Communications</h2>
              <div className="flex gap-3 flex-wrap">
                <Button
                  onClick={() => navigate('/customer-admin/send-email')}
                  className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white flex items-center gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Email
                </Button>
                <Button
                  onClick={() => navigate('/customer-admin/send-support-email')}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white flex items-center gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Support Email
                </Button>
                <Button
                  onClick={() => navigate('/customer-admin/template-manager')}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Manage Templates
                </Button>
                <Button
                  onClick={() => navigate('/customer-admin/ai-knowledge')}
                  data-testid="ai-knowledge-nav-btn"
                  className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-black hover:to-gray-800 text-white flex items-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  AI Chatbot Knowledge
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Accounts Email Composer Card - NEW */}
              <Card className="border-2 border-cyan-200 shadow-xl">
                <CardHeader className="bg-cyan-50">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-cyan-600" />
                    Accounts Email Composer
                  </CardTitle>
                  <CardDescription>
                    Professional email composer with contact & template management
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-cyan-600 rounded-full p-3">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Email Solution</h3>
                        <p className="text-gray-700 mb-4">
                          Advanced email composer with:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-cyan-600" />
                            Customer contact management
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-cyan-600" />
                            Pre-made email templates with HTML
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-cyan-600" />
                            Quick customer selection
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-cyan-600" />
                            Sent from accounts@netixfibernet.com
                          </li>
                        </ul>
                        <Button
                          onClick={() => navigate('/customer-admin/accounts-email')}
                          className="mt-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Open Accounts Composer
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Custom Email Card */}
              <Card className="border-2 border-blue-200 shadow-xl">
                <CardHeader className="bg-blue-50">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    Send Custom Email
                  </CardTitle>
                  <CardDescription>
                    Send personalized emails to customers with attachments
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-600 rounded-full p-3">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Composer</h3>
                        <p className="text-gray-700 mb-4">
                          Create and send custom emails with:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Custom subject and content
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            File attachments support
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Sent from Admin@netixfibernet.com
                          </li>
                        </ul>
                        <Button
                          onClick={() => navigate('/customer-admin/send-email')}
                          className="mt-4 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Open Email Composer
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Support Email Card */}
              <Card className="border-2 border-green-200 shadow-xl">
                <CardHeader className="bg-green-50">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-green-600" />
                    Send Support Email
                  </CardTitle>
                  <CardDescription>
                    Send support emails with pre-built templates
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-green-600 rounded-full p-3">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Support Email Composer</h3>
                        <p className="text-gray-700 mb-4">
                          Professional support emails with:
                        </p>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Pre-built support templates
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Ticket creation & updates
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Payment reminders & more
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Sent from support@netixfibernet.com
                          </li>
                        </ul>
                        <Button
                          onClick={() => navigate('/customer-admin/send-support-email')}
                          className="mt-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Open Support Email
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* ORDER MANAGEMENT SECTION */}
            <div className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-lg p-2">
                  <Activity className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Order Management</h2>
                  <p className="text-gray-600">Track and manage service orders from submission to activation</p>
                </div>
              </div>

              {/* Order Management Card */}
              <Card className="border-4 border-orange-300 shadow-2xl">
                <CardHeader className="bg-gradient-to-r from-orange-600 via-orange-500 to-red-500 text-white">
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Activity className="h-7 w-7" />
                    Professional Order Processing System
                  </CardTitle>
                  <CardDescription className="text-orange-50 text-base">
                    Complete lifecycle management from order receipt to service activation
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-8">
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {/* Stage 1: Orders Received */}
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-4 border-yellow-300 rounded-xl p-6 hover:shadow-xl transition-all">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-yellow-600 rounded-full p-3">
                          <Clock className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-yellow-900">Orders Received</h3>
                      </div>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span>View all new orders</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span>Customer details & contact info</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span>Service & plan selection</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span>Installation location details</span>
                        </li>
                      </ul>
                    </div>

                    {/* Stage 2: Provisioning */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-4 border-blue-300 rounded-xl p-6 hover:shadow-xl transition-all">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-600 rounded-full p-3">
                          <Settings className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-blue-900">Provisioning</h3>
                      </div>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span>Track service setup progress</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span>Update provisioning status</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span>Equipment & installation tracking</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span>Real-time status updates</span>
                        </li>
                      </ul>
                    </div>

                    {/* Stage 3: Activation */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 border-4 border-green-300 rounded-xl p-6 hover:shadow-xl transition-all">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-green-600 rounded-full p-3">
                          <CheckCircle className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-green-900">Activation</h3>
                      </div>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Configure service settings</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Set activation date</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Final quality checks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Mark service as active</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Key Features */}
                  <div className="bg-gradient-to-br from-gray-50 to-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Key Features & Capabilities:</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-orange-600 rounded-full p-2">
                          <Search className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Advanced Search & Filter</p>
                          <p className="text-sm text-gray-600">Search by name, order ID, mobile, email. Filter by status.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-orange-600 rounded-full p-2">
                          <Edit className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Edit Order Details</p>
                          <p className="text-sm text-gray-600">Update customer info, service, plan, and status anytime.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-orange-600 rounded-full p-2">
                          <TrendingUp className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Real-time Statistics</p>
                          <p className="text-sm text-gray-600">Dashboard with total, pending, provisioning, and active counts.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-orange-600 rounded-full p-2">
                          <FileText className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Admin Notes & Tracking</p>
                          <p className="text-sm text-gray-600">Add internal notes and track order history.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="text-center">
                    <Button
                      onClick={() => navigate('/customer-admin/order-management')}
                      size="lg"
                      className="bg-gradient-to-r from-orange-600 via-orange-500 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all"
                    >
                      <Activity className="h-6 w-6 mr-3" />
                      Open Order Management System
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* SUPPORT TICKETS MANAGEMENT SECTION */}
            <div className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-2">
                  <Ticket className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Support Tickets Management</h2>
                  <p className="text-gray-600">Create and manage customer support tickets with email notifications</p>
                </div>
              </div>

              <Card className="border-4 border-purple-300 shadow-2xl">
                <CardHeader className="bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-500 text-white">
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Ticket className="h-7 w-7" />
                    Admin Ticket Creation & Management
                  </CardTitle>
                  <CardDescription className="text-purple-50 text-base">
                    Create tickets on behalf of customers and manage all support requests
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-8">
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-4 border-purple-200 rounded-xl p-6">
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h3 className="text-lg font-bold text-purple-900 mb-3">Create Tickets</h3>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                            Create tickets for customers
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                            Set priority & category
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                            Auto-send email notifications
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-purple-900 mb-3">Manage Tickets</h3>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                            View all open tickets
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                            Reply & close tickets
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                            Filter by status & priority
                          </li>
                        </ul>
                      </div>
                    </div>
                    <Button
                      onClick={() => window.location.href = '/customer-admin/support-tickets'}
                      size="lg"
                      className="bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all w-full"
                    >
                      <Ticket className="h-6 w-6 mr-3" />
                      Open Support Tickets Management
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Advanced Bulk Email System Card */}
            <Card className="border-2 border-purple-200 shadow-xl">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-purple-600" />
                  Advanced Bulk Email System
                </CardTitle>
                <CardDescription>
                  Send marketing emails to multiple customers with professional templates
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-purple-600 rounded-full p-3">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Bulk Email Composer</h3>
                      <p className="text-gray-700 mb-4">
                        Send professional marketing emails with:
                      </p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Multiple recipient support
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Marketing templates with images
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          File & image attachments
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Custom subject and content
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Campaign statistics tracking
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Sent from notification@netixfibernet.com
                        </li>
                      </ul>
                      <Button
                        onClick={() => navigate('/customer-admin/bulk-email')}
                        className="mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Open Bulk Email System
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Push Notifications Card */}
            <Card className="border-2 border-orange-200 shadow-xl">
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-orange-600" />
                  Push Notifications
                </CardTitle>
                <CardDescription>
                  Send browser push notifications to website visitors
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-orange-600 rounded-full p-3">
                      <Bell className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Push Notification System</h3>
                      <p className="text-gray-700 mb-4">
                        Engage visitors with browser notifications:
                      </p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Create notification templates
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Target inactive visitors
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Add images and call-to-action
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Schedule notifications
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Track delivery statistics
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Auto-trigger based on behavior
                        </li>
                      </ul>
                      <Button
                        onClick={() => navigate('/customer-admin/push-notifications')}
                        className="mt-4 bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Manage Push Notifications
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Management & Reports Tab */}
        {activeTab === 'data-management' && (
          <DataManagement />
        )}
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <CustomerDetailModal 
          customer={selectedCustomer} 
          onClose={() => setSelectedCustomer(null)} 
        />
      )}

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onSuccess={(newCustomer) => {
          fetchCustomers();
          setShowAddCustomerModal(false);
        }}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          fetchCustomers();
          setShowBulkUploadModal(false);
        }}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        isOpen={showEditCustomerModal}
        onClose={() => {
          setShowEditCustomerModal(false);
          setCustomerToEdit(null);
        }}
        customer={customerToEdit}
        onSuccess={(updatedCustomer) => {
          fetchCustomers();
          setShowEditCustomerModal(false);
          setCustomerToEdit(null);
        }}
      />

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onSuccess={() => {
          fetchCustomers();
          setShowUploadModal(false);
          setSelectedCustomer(null);
        }}
      />

      {/* Create Ticket Modal */}
      <CreateTicketModal
        isOpen={showCreateTicketModal}
        onClose={() => setShowCreateTicketModal(false)}
        customers={customers}
        onSuccess={() => {
          fetchSupportTickets();
          setShowCreateTicketModal(false);
        }}
      />

      {/* Set Credentials Modal */}
      <SetCredentialsModal
        isOpen={showSetCredentialsModal}
        onClose={() => {
          setShowSetCredentialsModal(false);
          setCustomerForCredentials(null);
        }}
        customer={customerForCredentials}
        onSuccess={() => {
          fetchCustomers();
          setShowSetCredentialsModal(false);
          setCustomerForCredentials(null);
        }}
      />

      {/* Delete Customer Modal */}
      <DeleteCustomerModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCustomerToDelete(null);
        }}
        customer={customerToDelete}
        onSuccess={() => {
          fetchCustomers();
          setShowDeleteModal(false);
          setCustomerToDelete(null);
        }}
      />

      {/* Delete Invoice Confirmation Modal */}
      {showDeleteInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete Invoice</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this invoice?
              </p>
              {invoiceToDelete && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">Invoice Number</p>
                  <p className="font-bold text-emerald-600 text-lg">#{invoiceToDelete.invoiceNumber}</p>
                  <p className="text-sm text-gray-600 mt-2">Customer</p>
                  <p className="font-semibold">{invoiceToDelete.customerName}</p>
                  <p className="text-sm text-gray-600 mt-2">Amount</p>
                  <p className="font-bold">₹{invoiceToDelete.invoiceAmount?.toLocaleString() || '0'}</p>
                </div>
              )}
              <p className="text-red-600 text-sm mt-4 font-semibold">
                ⚠️ This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteInvoiceModal(false);
                  setInvoiceToDelete(null);
                }}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDeleteInvoice}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Invoice
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAdminDashboard;
